import { FormEvent, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  apiCreateAccount,
  apiDeleteAccount,
  apiListAccounts,
  apiUpdateAccount,
  type AccountRow,
} from "../api";
import { formatCents } from "../utils/money";
import { ACCOUNT_TYPE_ORDER, accountTypeLabel, compareAccountTypes } from "../accountTypes";

export function AccountsPage() {
  const [rows, setRows] = useState<AccountRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [type, setType] = useState<string>("checking");
  const [busy, setBusy] = useState(false);

  async function refresh() {
    const { accounts } = await apiListAccounts();
    setRows(accounts);
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await refresh();
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await apiCreateAccount({ name: name.trim(), type });
      setName("");
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error");
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string) {
    if (!confirm("Delete this account? Transactions will be deleted too.")) return;
    setError(null);
    try {
      await apiDeleteAccount(id);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error");
    }
  }

  if (loading) {
    return <p className="muted">Loading accounts…</p>;
  }

  return (
    <div className="stack">
      <div className="card">
        <h2 className="mt-0">Accounts</h2>
        <p className="muted">
          Bank accounts, cards, and cash buckets. New users get a <strong>Cash</strong> account automatically. Use{" "}
          <strong>Savings</strong> for goal balances, then move money on <Link to="/transfers">Transfers</Link> or log
          income and expenses on any account from{" "}
          <Link to="/transactions">Transactions</Link>.
        </p>
        {error ? <p className="error">{error}</p> : null}

        <form className="form-row" onSubmit={onCreate}>
          <label>
            Name
            <input
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
              placeholder="e.g. Main checking"
              required
            />
          </label>
          <label>
            Type
            <select value={type} onChange={(e) => setType(e.currentTarget.value)}>
              {ACCOUNT_TYPE_ORDER.map((t) => (
                <option key={t} value={t}>
                  {accountTypeLabel(t)}
                </option>
              ))}
            </select>
          </label>
          <button type="submit" disabled={busy}>
            Add account
          </button>
        </form>
      </div>

      <div className="card">
        <div className="table-scroll">
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Type</th>
              <th>Currency</th>
              <th className="right">Balance</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={5} className="muted">
                  No accounts yet.
                </td>
              </tr>
            ) : (
              rows.map((a) => (
                <AccountRowEditor key={a.id} account={a} onSaved={refresh} onDelete={() => remove(a.id)} />
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}

function typesForAccountRow(currentType: string): string[] {
  return [...new Set([...ACCOUNT_TYPE_ORDER, currentType])].sort(compareAccountTypes);
}

function AccountRowEditor({
  account,
  onSaved,
  onDelete,
}: {
  account: AccountRow;
  onSaved: () => Promise<void>;
  onDelete: () => void;
}) {
  const [name, setName] = useState(account.name);
  const [type, setType] = useState(account.type);
  const typeOptions = typesForAccountRow(type);

  async function save() {
    await apiUpdateAccount(account.id, { name: name.trim(), type });
    await onSaved();
  }

  return (
    <tr>
      <td>
        <input
          className="input-inline"
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          onBlur={() => void save()}
        />
      </td>
      <td>
        <select
          value={type}
          onChange={(e) => {
            setType(e.currentTarget.value);
            void (async () => {
              await apiUpdateAccount(account.id, { type: e.currentTarget.value });
              await onSaved();
            })();
          }}
        >
          {typeOptions.map((t) => (
            <option key={t} value={t}>
              {accountTypeLabel(t)}
            </option>
          ))}
        </select>
      </td>
      <td className="mono">{account.currency}</td>
      <td className={`right mono ${account.balanceCents >= 0 ? "amt-in" : "amt-out"}`}>
        {formatCents(account.balanceCents)}
      </td>
      <td className="right">
        <button type="button" className="btn-ghost btn-sm" onClick={onDelete}>
          Delete
        </button>
      </td>
    </tr>
  );
}
